import { useState, useEffect, useRef } from 'react';
import TagBadge from './TagBadge';

interface SystemTag {
  _id: string;
  name: string;
  color: string;
  description?: string;
}

interface TagSelectorProps {
  selectedTags: string[];
  availableTags: SystemTag[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

export default function TagSelector({ selectedTags, availableTags, onChange, disabled }: TagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleTag = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      onChange(selectedTags.filter(t => t !== tagName));
    } else {
      onChange([...selectedTags, tagName]);
    }
  };

  const handleRemoveTag = (tagName: string) => {
    onChange(selectedTags.filter(t => t !== tagName));
  };

  const getTagByName = (name: string): SystemTag | undefined => {
    return availableTags.find(t => t.name === name);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`min-h-[38px] p-1.5 border rounded-md flex flex-wrap gap-1 cursor-pointer ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-indigo-500'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedTags.length === 0 ? (
          <span className="text-gray-400 dark:text-gray-500 text-sm px-1">Select tags...</span>
        ) : (
          selectedTags.map(tagName => {
            const tag = getTagByName(tagName);
            return tag ? (
              <TagBadge
                key={tagName}
                name={tag.name}
                color={tag.color}
                onRemove={disabled ? undefined : () => handleRemoveTag(tagName)}
              />
            ) : (
              <span
                key={tagName}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {tagName}
              </span>
            );
          })
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {availableTags.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
              No tags available. Create tags in Settings.
            </div>
          ) : (
            availableTags.map(tag => {
              const isSelected = selectedTags.includes(tag.name);
              return (
                <div
                  key={tag._id}
                  className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  }`}
                  onClick={() => handleToggleTag(tag.name)}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100">{tag.name}</span>
                  </div>
                  {isSelected && (
                    <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
